import { type DateFormatPresetId, dateFormatPresets, formatDateWithPreset } from '@/helpers/dates';
import { useAppStore } from '@/stores/app';
import Dialog from '@nautilusbio/nb-design-system/Dialog';
import RadioGroup from '@nautilusbio/nb-design-system/RadioGroup';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { Cog } from 'lucide-react';
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import sidebarStyles from '../Sidebar/styles.module.css';

import styles from './styles.module.css';

/**
 * Fixed sample date for consistent preview across all format options.
 * Using a fixed date ensures predictable examples for users.
 */
const SAMPLE_DATE = new Date(); // Dec 31, 2024, 5:00 PM

interface SettingsProps {
  id: string;
  title?: string;
}

const Settings: FC<SettingsProps> = ({ id, title }) => {
  const { t } = useTranslation('general');
  const dataTestId = `${id}-dialog`;

  // Use useShallow to prevent unnecessary re-renders when other store values change
  const { dateFormatPresetId, setDateFormatPresetId } = useAppStore(
    useShallow(state => ({
      dateFormatPresetId: state.dateFormatPresetId,
      setDateFormatPresetId: state.setDateFormatPresetId,
    })),
  );

  // Memoize the change handler to prevent recreation on each render
  const handleDateFormatChange = useCallback(
    (value: string) => {
      const presetId = value as DateFormatPresetId;
      setDateFormatPresetId(presetId);

      // Show success toast with the selected format name
      const formatLabel = t(`date_format_presets.${presetId}.label`);
      toast.success(t('date_format_updated', { format: formatLabel }));
    },
    [setDateFormatPresetId, t],
  );

  // Memoize radio options to prevent recreation unless translations change
  const radioOptions = useMemo(
    () =>
      dateFormatPresets.map(preset => ({
        value: preset.id,
        children: (
          <Stack gap={1}>
            <Text as="div" className={styles.optionLabel} size="sm">
              {t(`date_format_presets.${preset.id}.label`)}
            </Text>
            <Text as="div" className={styles.optionDescription} size="sm">
              {t(`date_format_presets.${preset.id}.description`)}
            </Text>
            <Text as="div" className={styles.optionExample} size="xs">
              {formatDateWithPreset(SAMPLE_DATE, preset.id)}
            </Text>
          </Stack>
        ),
      })),
    [t], // Re-create if translation function changes (language switch)
  );

  // Memoize trigger content to prevent recreation
  const triggerContent = useMemo(
    () => (
      <>
        <Cog size={14} /> {title || t('settings')}
      </>
    ),
    [title, t],
  );

  // Memoize dialog content to prevent recreation unless dependencies change
  const dialogContent = useMemo(
    () => (
      <div className={styles.root}>
        <Stack as="section" className={styles.section} gap={4}>
          <Stack gap={1}>
            <Text as="h2" className={styles.sectionTitle} size="lg">
              {t('date_time_format')}
            </Text>
            <Text as="p" className={styles.sectionHint} size="sm">
              {t('applies_across_app')}
            </Text>
          </Stack>

          <Text as="p" className={styles.sectionDescription} size="sm">
            {t('date_format_description')}
          </Text>

          <RadioGroup
            aria-label={t('choose_date_time_format')}
            classNames={{
              label: styles.visuallyHidden,
            }}
            data-testid="date-format-radio-group"
            gap={3}
            id="date-format-preset"
            label={t('date_format_options')}
            onChange={handleDateFormatChange}
            options={radioOptions}
            orientation="vertical"
            value={dateFormatPresetId}
          />
        </Stack>
      </div>
    ),
    [t, handleDateFormatChange, radioOptions, dateFormatPresetId],
  );

  return (
    <Dialog
      classNames={{ overlay: sidebarStyles.dialogOverlay }}
      content={dialogContent}
      data-testid={dataTestId}
      title={title || t('settings')}
      triggerProps={{
        children: triggerContent,
        fullWidth: true,
        size: 'sm',
        visual: 'default',
      }}
    />
  );
};

Settings.displayName = 'Settings';

export default Settings;
